import {
  FilterXIcon,
  LinkIcon,
  ListFilterIcon,
  PlusIcon,
  RefreshCwIcon,
  SearchIcon,
  TargetIcon,
} from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { getLead, LeadSort, type Lead, type LeadSummary } from '@/api/leads'
import { useApiErrorMessage } from '@/api/errors'
import { Role } from '@/auth/roles'
import { useAuth } from '@/auth/useAuth'
import { ClearFiltersButton } from '@/components/ClearFiltersButton'
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardFooter } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { ActivatableRow } from '@/components/layout/ActivatableRow'
import { PageHeader } from '@/components/layout/PageHeader'
import { Pagination } from '@/components/layout/Pagination'
import { SortableHead } from '@/components/layout/SortableHead'
import {
  EDGE_PADDED_TABLE,
  FULL_HEIGHT_CARD,
  PHONE_SUMMARY,
  PHONE_WRAPPING_CELL,
  SCROLLING_TABLE,
  STICKY_TABLE_HEAD,
  WIDE_ONLY,
} from '@/components/layout/tableStyles'
import { AssigneeFilter } from '@/components/leads/AssigneeFilter'
import { AssigneeName } from '@/components/leads/AssigneeName'
import { AssignLeadDialog } from '@/components/leads/AssignLeadDialog'
import { LeadDialog } from '@/components/leads/LeadDialog'
import { LeadRowActions } from '@/components/leads/LeadRowActions'
import { LeadSelectionBar } from '@/components/leads/LeadSelectionBar'
import { TradeFairFilter } from '@/components/leads/TradeFairFilter'
import { useColleagueDirectory } from '@/hooks/useColleagues'
import { useLeadList } from '@/hooks/useLeadList'
import { formatDate } from '@/lib/dates'
import { cn } from '@/lib/utils'

const COLUMN_COUNT = 6

/** One instance, so that "nothing is ticked" is the same value on every render. */
const EMPTY_SELECTION: ReadonlySet<string> = new Set()

/**
 * The leads this user may see — whoever wrote it, whoever it was handed to, and any administrator.
 *
 * That decision is the backend's and is made **in the query**, so the count in the footer is the
 * count of what this user may see. There is deliberately no switch here that could widen it.
 *
 * A fair produces leads by the hundred, so the list is paged rather than scrolled: the filters sit
 * above the table, the page controls below it, and nothing on screen depends on having loaded
 * everything.
 */
export function LeadsPage() {
  const { t } = useTranslation()
  const { hasRole, user: me } = useAuth()
  const errorMessage = useApiErrorMessage()
  const list = useLeadList()
  // Loaded with the page, unlike in the dialogs: every row shows whom it is assigned to, so the
  // names are needed the moment the first row is.
  const directory = useColleagueDirectory(true)

  // An administrator sees everybody's, so they are the only one for whom "only mine" narrows
  // anything. For a salesperson it would be a control that never changes the result.
  const isAdmin = hasRole(Role.ADMIN)

  const [dialogOpen, setDialogOpen] = useState(false)
  /**
   * Whether the filters are showing on a phone. Five controls stacked above the list took half
   * the screen there before the first lead, so a phone gets the search and a button that folds
   * the rest out. From a tablet up they are always there and this is never consulted.
   */
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [editing, setEditing] = useState<Lead | null>(null)
  /**
   * The ticks, and the state of the list they were made in.
   *
   * Paging, filtering, searching or reordering drops them — and drops them for good. Keying this
   * on the rows themselves would look right and be wrong: going to page two and back brings the
   * same rows, so the ticks would come back with them, and the next batch would reach leads
   * whoever pressed it had long forgotten ticking.
   */
  const [ticked, setTicked] = useState<{ state: string; ids: ReadonlySet<string> }>({
    state: '',
    ids: EMPTY_SELECTION,
  })
  const [assigning, setAssigning] = useState<LeadSummary | null>(null)

  const leads = list.page?.leads ?? []
  const total = list.page?.total ?? 0

  // Everything that decides which rows are on screen. Adjusted during the render that brings a
  // new one in, which is what makes the ticks gone rather than merely hidden.
  const listState = JSON.stringify([
    list.search,
    list.filters,
    list.sort,
    list.direction,
    list.pageIndex,
    list.pageSize,
  ])
  if (ticked.state !== listState) {
    setTicked({ state: listState, ids: EMPTY_SELECTION })
  }

  const current = ticked.state === listState ? ticked.ids : EMPTY_SELECTION
  const selected = leads.filter((lead) => current.has(lead.id)).map((lead) => lead.id)
  const allTicked = leads.length > 0 && selected.length === leads.length

  function tick(id: string, on: boolean) {
    const next = new Set(current)
    if (on) {
      next.add(id)
    } else {
      next.delete(id)
    }
    setTicked({ state: listState, ids: next })
  }

  function tickAll(on: boolean) {
    setTicked({
      state: listState,
      ids: on ? new Set(leads.map((lead) => lead.id)) : EMPTY_SELECTION,
    })
  }

  function openCreate() {
    setEditing(null)
    setDialogOpen(true)
  }

  /**
   * The list carries only what a row needs, so editing fetches the whole lead first — the frozen
   * copy, the contacts, the interests and the history are not in the summary.
   */
  async function openEdit(id: string) {
    try {
      setEditing(await getLead(id))
      setDialogOpen(true)
    } catch (error) {
      toast.error(errorMessage(error, t('leads.loadOneFailed')))
    }
  }

  return (
    <TooltipProvider>
      <PageHeader title={t('leads.title')}>
        <Button onClick={openCreate}>
          <PlusIcon />
          {t('leads.actions.create')}
        </Button>
      </PageHeader>

      <Card className={FULL_HEIGHT_CARD}>
        {/* The selection bar lies over the toolbar rather than being inserted under it. Pushed in
            between, it shoved the table down by a row's height the moment the first box was
            ticked — right under the pointer that was about to tick the second. On top it changes
            nothing below it, and hides only controls that would drop the selection anyway: the
            ticks belong to the list as filtered, and a new filter is a new list. */}
        <div className="relative shrink-0">
          <div
            className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b p-4"
            // Out of the tab order and the pointer's reach while the bar covers it.
            inert={selected.length > 0}
          >
            {/* On a phone the search shrinks to keep Filter and reload beside it, on one line. */}
            <div className="relative min-w-56 flex-1 max-md:min-w-0">
              <SearchIcon
                className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2"
                aria-hidden
              />
              <Input
                type="search"
                className="pl-8"
                aria-label={t('leads.searchLabel')}
                placeholder={t('leads.searchPlaceholder')}
                value={list.search}
                onChange={(event) => list.setSearch(event.target.value)}
              />
            </div>

            <Button
              variant="outline"
              className="md:hidden"
              aria-expanded={filtersOpen}
              aria-controls="lead-filters"
              onClick={() => setFiltersOpen((open) => !open)}
            >
              <ListFilterIcon />
              {t('leads.filters')}
              {/* Folded away is no reason not to know that the list is narrowed. */}
              {list.filtered && <span className="bg-primary size-1.5 rounded-full" aria-hidden />}
            </Button>

            {/* `contents` takes the wrapper out of the layout, so from a tablet up the filters sit
                in the toolbar exactly as if it were not there. */}
            <div
              id="lead-filters"
              className={cn(
                'contents max-md:[&>[data-slot=select-trigger]]:w-full',
                !filtersOpen && 'max-md:hidden',
              )}
            >
              <TradeFairFilter value={list.tradeFairId} onChange={list.setTradeFairId} />

              <AssigneeFilter
                value={list.assignee}
                onChange={list.setAssignee}
                colleagues={directory.colleagues}
                meId={me?.id}
              />

              {isAdmin && (
                <div className="flex items-center gap-2 px-1">
                  <Checkbox
                    id="only-mine"
                    checked={list.onlyMine}
                    onCheckedChange={(checked) => list.setOnlyMine(checked === true)}
                  />
                  <Label htmlFor="only-mine" className="text-sm font-normal">
                    {t('leads.onlyMine')}
                  </Label>
                </div>
              )}

              {/* For everybody, unlike "only mine": taking a company into the master data is the
                  one piece of state on a lead that somebody still has to act on, whoever wrote
                  it. */}
              <div className="flex items-center gap-2 px-1">
                <Checkbox
                  id="only-unlinked"
                  checked={list.onlyUnlinked}
                  onCheckedChange={(checked) => list.setOnlyUnlinked(checked === true)}
                />
                <Label htmlFor="only-unlinked" className="text-sm font-normal">
                  {t('leads.onlyUnlinked')}
                </Label>
              </div>

              <ClearFiltersButton
                active={list.filtered}
                label={t('leads.clearFilters')}
                onClick={list.clearFilters}
              />
            </div>

            <Button variant="outline" size="icon" aria-label={t('leads.reload')} onClick={list.reload}>
              <RefreshCwIcon />
            </Button>
          </div>

          {selected.length > 0 && (
            <LeadSelectionBar
              selected={selected}
              onChanged={list.reload}
              onClear={() => setTicked({ state: listState, ids: EMPTY_SELECTION })}
            />
          )}
        </div>

        {list.error ? (
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            <Alert variant="destructive">
              <AlertTitle>{t('leads.loadFailed')}</AlertTitle>
              <AlertDescription>
                {errorMessage(list.error, t('common.unexpectedError'))}
              </AlertDescription>
              <AlertAction>
                <Button variant="outline" size="sm" onClick={list.reload}>
                  {t('leads.retry')}
                </Button>
              </AlertAction>
            </Alert>
          </div>
        ) : (
          <Table
            className={EDGE_PADDED_TABLE}
            containerClassName={SCROLLING_TABLE}
            // The rows on screen are the previous page while the next one loads. This is what
            // says so to a screen reader; the dimming below is what says it to everybody else.
            aria-busy={list.loading}
          >
            <TableHeader className={STICKY_TABLE_HEAD}>
              <TableRow>
                <TableHead className="w-0">
                  <Checkbox
                    // Indeterminate while only part of the page is ticked: "not ticked" would
                    // claim the opposite of what is on screen.
                    checked={allTicked || (selected.length > 0 && 'indeterminate')}
                    disabled={leads.length === 0}
                    aria-label={t('leadSelection.selectAll')}
                    onCheckedChange={(checked) => tickAll(checked === true)}
                  />
                </TableHead>
                <SortableHead
                  column={LeadSort.COMPANY_NAME}
                  sort={list.sort}
                  direction={list.direction}
                  onToggle={list.toggleSort}
                >
                  {t('leads.columns.company')}
                </SortableHead>
                <SortableHead
                  column={LeadSort.TRADE_FAIR}
                  sort={list.sort}
                  direction={list.direction}
                  onToggle={list.toggleSort}
                  className={WIDE_ONLY}
                >
                  {t('leads.columns.tradeFair')}
                </SortableHead>
                {/* Not sortable: the rows carry an id, and an order by id is an order nobody can
                    read. A fixed width, so the names arriving after the rows move nothing. */}
                <TableHead className={cn('w-48', WIDE_ONLY)}>
                  {t('leads.columns.assignee')}
                </TableHead>
                <SortableHead
                  column={LeadSort.CAPTURED_AT}
                  sort={list.sort}
                  direction={list.direction}
                  onToggle={list.toggleSort}
                  className={WIDE_ONLY}
                >
                  {t('leads.columns.captured')}
                </SortableHead>
                <TableHead className="w-0 text-right">
                  <span className="sr-only">{t('leads.columns.actions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody
              // Held back while the next page is on its way, rather than blanked: the rows are
              // still true, they are just no longer the answer to what was asked.
              className={list.loading && leads.length > 0 ? 'opacity-60 transition-opacity' : undefined}
            >
              {list.loading && leads.length === 0 && <LoadingRows />}

              {!list.loading && leads.length === 0 && (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={COLUMN_COUNT} className="h-48">
                    <div className="text-muted-foreground mx-auto flex max-w-sm flex-col items-center justify-center gap-2 text-center">
                      <TargetIcon className="size-6" aria-hidden />
                      <p className="text-foreground text-sm font-medium">
                        {list.search
                          ? t('leads.emptySearch', { search: list.search })
                          : list.filtered
                            ? t('leads.emptyFilter')
                            : t('leads.empty')}
                      </p>
                      <p className="text-xs">
                        {list.filtered ? t('leads.emptyFilterHint') : t('leads.emptyHint')}
                      </p>
                      {/* The way out of the state somebody is looking at: back to the whole list
                          when a filter emptied it, and the first lead when nothing has been
                          captured yet. */}
                      {list.filtered ? (
                        <Button variant="outline" size="sm" className="mt-2" onClick={list.clearFilters}>
                          <FilterXIcon />
                          {t('leads.clearFilters')}
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm" className="mt-2" onClick={openCreate}>
                          <PlusIcon />
                          {t('leads.actions.create')}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {leads.map((lead) => (
                <ActivatableRow
                  key={lead.id}
                  data-testid={`lead-${lead.id}`}
                  data-state={current.has(lead.id) ? 'selected' : undefined}
                  onActivate={() => void openEdit(lead.id)}
                >
                  <TableCell onClick={(event) => event.stopPropagation()}>
                    <Checkbox
                      checked={current.has(lead.id)}
                      aria-label={t('leadSelection.selectRow')}
                      onCheckedChange={(checked) => tick(lead.id, checked === true)}
                    />
                  </TableCell>
                  <TableCell className={PHONE_WRAPPING_CELL}>
                    <div className="flex flex-col">
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium">
                        {lead.companyName}
                        {/* The copy has not been taken into the master data yet — the one piece of
                            state on a lead that somebody has to act on. */}
                        {!lead.linkedToCustomer && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="outline" className="gap-1 font-normal">
                                <LinkIcon aria-hidden />
                                {t('leads.notLinked')}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>{t('leads.notLinkedHint')}</TooltipContent>
                          </Tooltip>
                        )}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        {lead.contactName ?? t('leads.noContact')}
                      </span>
                      <span className={PHONE_SUMMARY}>
                        {lead.tradeFairName} · {formatDate(lead.capturedAt.slice(0, 10))}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className={cn('text-muted-foreground', WIDE_ONLY)}>
                    {lead.tradeFairName}
                  </TableCell>
                  <TableCell className={cn('max-w-48', WIDE_ONLY)}>
                    <AssigneeName
                      assigneeId={lead.assigneeId}
                      colleagues={directory.colleagues}
                      loaded={directory.loaded}
                    />
                  </TableCell>
                  <TableCell className={cn('tabular-nums whitespace-nowrap', WIDE_ONLY)}>
                    {formatDate(lead.capturedAt.slice(0, 10))}
                  </TableCell>
                  <TableCell
                    className="text-right"
                    // The row opens the lead; the menu inside it must not do both.
                    onClick={(event) => event.stopPropagation()}
                  >
                    <LeadRowActions
                      lead={lead}
                      isOwner={isAdmin || lead.ownerId === me?.id}
                      onEdit={() => void openEdit(lead.id)}
                      onAssign={() => setAssigning(lead)}
                      onChanged={list.reload}
                    />
                  </TableCell>
                </ActivatableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {!list.error && total > 0 && (
          <CardFooter className="block shrink-0">
            <Pagination
              pageIndex={list.pageIndex}
              pageCount={list.pageCount}
              pageSize={list.pageSize}
              range={list.range}
              onPageChange={list.setPageIndex}
              onPageSizeChange={list.setPageSize}
            />
          </CardFooter>
        )}
      </Card>

      <AssignLeadDialog
        lead={assigning}
        onOpenChange={(open) => !open && setAssigning(null)}
        onAssigned={list.reload}
      />

      <LeadDialog
        open={dialogOpen}
        lead={editing}
        onOpenChange={setDialogOpen}
        onSaved={list.reload}
      />
    </TooltipProvider>
  )
}

function LoadingRows() {
  return (
    <>
      {Array.from({ length: 5 }, (_, index) => (
        <TableRow key={index} className="hover:bg-transparent">
          <TableCell colSpan={COLUMN_COUNT} className="py-3">
            <Skeleton className="h-8 w-full" />
          </TableCell>
        </TableRow>
      ))}
    </>
  )
}
